"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "kitchen-space-planner",
  "refrigerator-size-calculator",
  "beverage-cooler-size-calculator",
  "energy-cost-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Under-Counter Refrigerator Fit Calculator",
  category: "Space Planning" as const,
  mediumDescription:
    "Measure twice, install once. Enter your cabinet opening and the fridge spec, and we'll check width, height, and depth against the ventilation clearances most under-counter manufacturers require.",
  faqs: [
    { question: "How much clearance does an under-counter fridge need?", answer: "Most manufacturers require at least 1/2 inch on each side, 1 inch above for ventilation, and 1 inch behind. Built-in models with front-vented compressors can sit flush on the sides, but they still need front airflow at the toe-kick." },
    { question: "What's the standard under-counter fridge size?", answer: "The most common opening is 24 inches wide × 34 inches tall × 24 inches deep. Some 15-inch and 18-inch beverage centers also exist for narrow runs and islands." },
    { question: "Why does ventilation matter so much?", answer: "Compressors release heat. If that heat can't escape, the unit runs longer, uses more electricity, and the compressor wears out years sooner. Insufficient ventilation is the #1 cause of premature under-counter fridge failure." },
    { question: "Can I install a freestanding fridge in a built-in opening?", answer: "Only if you give it the manufacturer's required clearance — usually 2 inches on the sides and back. A freestanding model jammed into a tight built-in opening will overheat. If the cabinet is tight, buy a true built-in (front-vented) model." },
    { question: "What about door swing clearance?", answer: "Plan for the door to swing 90° plus a few inches for the handle. Adjacent walls or cabinets within ~26 inches of a 24-inch door will block full-open access to drawers and bins." },
    { question: "Should I account for baseboards and trim?", answer: "Yes. Measure the actual opening from finished surface to finished surface — baseboards, quarter-round, and door casings all eat into usable depth and width. The published cabinet spec is rarely the same as the installed opening." },
  ],
  relatedTools: ["kitchen-space-planner", "refrigerator-size-calculator", "beverage-cooler-size-calculator"],
}

export function UnderCounterFitCalculatorPageClient() {
  const [spaceWidth, setSpaceWidth] = useState("24")
  const [spaceHeight, setSpaceHeight] = useState("34")
  const [spaceDepth, setSpaceDepth] = useState("24")
  const [fridgeWidth, setFridgeWidth] = useState("23.5")
  const [fridgeHeight, setFridgeHeight] = useState("33")
  const [fridgeDepth, setFridgeDepth] = useState("22")
  const [doorSwing, setDoorSwing] = useState("right")

  // Original calc logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // Inch bounds are the physical envelope of a cabinet opening and of the
    // appliance going into it — the narrowest units in this category are the
    // 15-inch beverage centers named in the FAQ below, so these floors sit
    // under anything real. They matter because a negative or near-zero FRIDGE
    // dimension made the gap large and positive, and the panel answered
    // "Fits with proper clearance". FRIDGE-384.
    const sWidth = readQuantity(spaceWidth, { min: 10, max: 60 })
    const sHeight = readQuantity(spaceHeight, { min: 12, max: 96 })
    const sDepth = readQuantity(spaceDepth, { min: 10, max: 48 })
    const fWidth = readQuantity(fridgeWidth, { min: 10, max: 60 })
    const fHeight = readQuantity(fridgeHeight, { min: 12, max: 96 })
    const fDepth = readQuantity(fridgeDepth, { min: 10, max: 48 })

    if (
      sWidth === null || sHeight === null || sDepth === null ||
      fWidth === null || fHeight === null || fDepth === null
    ) return null

    const widthClearance = 0.5
    const heightClearance = 1
    const depthClearance = 1

    const widthGap = sWidth - fWidth
    const heightGap = sHeight - fHeight
    const depthGap = sDepth - fDepth

    const widthFit = widthGap >= widthClearance
    const heightFit = heightGap >= heightClearance
    const depthFit = depthGap >= depthClearance

    let overallFit: "perfect" | "tight" | "no-fit" = "perfect"
    if (!widthFit || !heightFit || !depthFit) overallFit = "no-fit"
    else if (widthGap < 1 || heightGap < 2 || depthGap < 2) overallFit = "tight"

    const warnings: string[] = []
    if (!widthFit) warnings.push("Fridge is too wide for the space")
    else if (widthGap < 1) warnings.push("Very tight width clearance")
    if (!heightFit) warnings.push("Fridge is too tall for the space")
    else if (heightGap < 2) warnings.push("Insufficient top clearance for ventilation")
    if (!depthFit) warnings.push("Fridge is too deep for the space")
    else if (depthGap < 2) warnings.push("Insufficient rear clearance for airflow")

    if (doorSwing === "right" || doorSwing === "left") {
      warnings.push(`Verify ${doorSwing}-side door has 26+ inches of swing clearance`)
    }

    return {
      overallFit,
      widthGap: Math.round(widthGap * 100) / 100,
      heightGap: Math.round(heightGap * 100) / 100,
      depthGap: Math.round(depthGap * 100) / 100,
      warnings,
    }
  }, [spaceWidth, spaceHeight, spaceDepth, fridgeWidth, fridgeHeight, fridgeDepth, doorSwing])

  const fitColor =
    result?.overallFit === "perfect" ? "from-emerald-500 to-emerald-700" :
    result?.overallFit === "tight" ? "from-amber-500 to-orange-600" :
    result?.overallFit === "no-fit" ? "from-rose-500 to-red-700" : "from-[#0469f0] to-[#0053c4]"

  const fitLabel =
    result?.overallFit === "perfect" ? "Fits with proper clearance" :
    result?.overallFit === "tight" ? "Fits — but tight" :
    result?.overallFit === "no-fit" ? "Does not fit" : "Enter measurements"

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Will it"
      heroTitleItalic="actually"
      heroTitlePost="fit?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~45 sec", "Manufacturer ventilation clearances"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "1/2\"", label: "Side clearance", note: "Each side" },
            { value: "1\"", label: "Top clearance", note: "Ventilation" },
            { value: "1\"", label: "Rear clearance", note: "Airflow" },
            { value: "26\"", label: "Door swing arc", note: "24\" door" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "under-counter-refrigerator-fit-calculator")}
      cta={{
        title: "Shop under-counter and beverage center fridges.",
        body: "Browse undercounter refrigerators with full dimension specs — width, height, depth, and ventilation requirements per model.",
        buttonLabel: "Browse undercounter fridges",
        buttonHref: "/collections/beverage-coolers",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Cabinet opening,
                <br />
                fridge spec, swing.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Measure the finished opening, not the cabinet box. All dimensions in inches.
              </p>

              <div className="mt-7 space-y-5">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Space W
                    </label>
                    <input
                      type="number"
                      min={10}
                      max={60}
                      step="0.25"
                      value={spaceWidth}
                      onChange={(e) => setSpaceWidth(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Space H
                    </label>
                    <input
                      type="number"
                      min={12}
                      max={96}
                      step="0.25"
                      value={spaceHeight}
                      onChange={(e) => setSpaceHeight(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Space D
                    </label>
                    <input
                      type="number"
                      min={10}
                      max={48}
                      step="0.25"
                      value={spaceDepth}
                      onChange={(e) => setSpaceDepth(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Fridge W
                    </label>
                    <input
                      type="number"
                      min={10}
                      max={60}
                      step="0.25"
                      value={fridgeWidth}
                      onChange={(e) => setFridgeWidth(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Fridge H
                    </label>
                    <input
                      type="number"
                      min={12}
                      max={96}
                      step="0.25"
                      value={fridgeHeight}
                      onChange={(e) => setFridgeHeight(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Fridge D
                    </label>
                    <input
                      type="number"
                      min={10}
                      max={48}
                      step="0.25"
                      value={fridgeDepth}
                      onChange={(e) => setFridgeDepth(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Door swing
                  </label>
                  <select
                    value={doorSwing}
                    onChange={(e) => setDoorSwing(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="right">Right hinge</option>
                    <option value="left">Left hinge</option>
                    <option value="reversible">Reversible</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Account for adjacent walls and cabinets.
                  </div>
                </div>
                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className={`rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1 bg-gradient-to-br ${fitColor}`}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Fit verdict
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 5.5vw, 60px)",
                      letterSpacing: "-0.04em",
                      lineHeight: 0.95,
                    }}
                  >
                    {fitLabel}
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Width gap
                      </div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">
                        {result?.widthGap ?? "—"}&quot;
                      </div>
                    </div>
                    <div className="rounded-lg p-3 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Top gap
                      </div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">
                        {result?.heightGap ?? "—"}&quot;
                      </div>
                    </div>
                    <div className="rounded-lg p-3 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Rear gap
                      </div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">
                        {result?.depthGap ?? "—"}&quot;
                      </div>
                    </div>
                  </div>
                  {result?.warnings && result.warnings.length > 0 && (
                    <div className="mt-6 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold">
                        Notes
                      </div>
                      {result.warnings.map((w) => (
                        <div key={w} className="text-[13px] text-white/85 leading-[1.4]">
                          • {w}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the size"
          title="Counter-depth refrigerators"
          description="Built-in compatible models with front-vented compressors that handle tight clearances."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Clearances from <em className="italic text-[#ff9951]">manufacturer</em> install guides.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Most under-counter and built-in refrigerator install manuals require the same baseline clearances. Tight installations void warranty and shorten compressor life.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Width clearance",
                  d: "1/2 inch on each side, minimum, for freestanding units. Built-in (front-vented) models can sit flush.",
                },
                {
                  n: "02",
                  t: "Height & rear clearance",
                  d: "1 inch on top and 1 inch behind, minimum, for ventilation. Built-in models still need front airflow at the toe-kick.",
                },
                {
                  n: "03",
                  t: "Door swing",
                  d: "Plan for 90° swing plus handle. A 24-inch door needs ~26 inches of swing arc to fully open drawers.",
                },
                {
                  n: "04",
                  t: "Verdict tiers",
                  d: "Perfect: meets all clearances + 1\" buffer. Tight: meets minimums but no buffer. No-fit: any dimension below minimum.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges for tight kitchens"
          description="If your built-in opening is too small, counter-depth full-size models offer the closest flush-look alternative."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
